import apiClient, { handleApiResponse, handleApiError } from './apiClient';

// ---------------------------------------------------------------------------
// SuccessResponse unwrap helper (mirrors dynamicFormsService)
// ---------------------------------------------------------------------------

const unwrap = (resp) => {
  if (!resp) return { success: false };
  const payload = resp.data ?? resp;
  if (payload.success !== undefined) return payload;
  return { success: true, data: payload };
};

/**
 * Modern RBAC Service
 * 
 * This service handles all role-based access control operations using modern REST endpoints.
 * It includes response transformation for legacy compatibility.
 */

class RBACService {
    constructor() {
        this.rolesUrl = '/rbac/roles';
        this.permissionsUrl = '/rbac/permissions';
        // Basic in-memory cache/dedupe to avoid hammering backend
        this.cache = new Map();
        this.pending = new Map();
        this.cacheTTL = 60 * 1000; // 60s
    }

    getCached(key) {
        const entry = this.cache.get(key);
        if (!entry) return null;
        if (Date.now() - entry.t > this.cacheTTL) {
            this.cache.delete(key);
            return null;
        }
        return entry.v;
    }

    setCached(key, value) {
        this.cache.set(key, { v: value, t: Date.now() });
    }

    dedupe(key, factory) {
        if (this.pending.has(key)) return this.pending.get(key);
        const p = factory().finally(() => this.pending.delete(key));
        this.pending.set(key, p);
        return p;
    }

    clearCache(key) {
        if (key) {
            this.cache.delete(key);
        } else {
            this.cache.clear();
        }
    }

    clearUserCache(userId) {
        this.clearCache(`user_comprehensive_${userId}`);
        this.clearCache(`user_permissions_${userId}`);
        this.clearCache(`roles_${userId}`);
        this.clearCache(`perms_${userId}`);
    }

    // ============================================================================
    // ROLE MANAGEMENT
    // ============================================================================

    /**
     * Get paginated list of roles
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Transformed role list response
     */
    async getRoles(params = {}) {
        try {
            const sanitizedParams = Object.fromEntries(
                Object.entries(params).filter(([_, value]) => value != null)
            );
            const response = await apiClient.get(this.rolesUrl, { params: sanitizedParams });

            // The backend now returns a consistent { items, total, page, ... } structure.
            // We adapt it to the frontend's expected { data, total, ... } structure.
            const { items, total, page, page_size, has_more } = response.data;

            return {
                success: true,
                data: items, // The array of roles for the DataTable
                total: total,
                page: page,
                pageSize: page_size,
                hasMore: has_more,
                message: "Roles retrieved successfully"
            };

        } catch (error) {
            return {
                success: false,
                data: [],
                total: 0,
                message: error.response?.data?.detail || "Failed to fetch roles"
            };
        }
    }

    /**
     * Get single role by ID
     * @param {string} roleId - Role ID
     * @returns {Promise<Object>} Transformed role response
     */
    async getRoleById(roleId) {
        try {
            const response = await apiClient.get(`${this.rolesUrl}/${roleId}`);
            return unwrap(response);
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Create new role
     * @param {Object} roleData - Role data
     * @returns {Promise<Object>} Transformed role response
     */
    async createRole(roleData) {
        try {
            const response = await apiClient.post(this.rolesUrl, roleData);
            return unwrap(response);
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Update existing role
     * @param {string} roleId - Role ID
     * @param {Object} roleData - Updated role data
     * @returns {Promise<Object>} Transformed role response
     */
    async updateRole(roleId, roleData) {
        try {
            const response = await apiClient.put(`${this.rolesUrl}/${roleId}`, roleData);
            return unwrap(response);
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Delete role
     * @param {string} roleId - Role ID
     * @returns {Promise<Object>} Success response
     */
    async deleteRole(roleId) {
        try {
            const response = await apiClient.delete(`${this.rolesUrl}/${roleId}`);
            return unwrap(response);
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Assign permissions to role
     * @param {string} roleId - Role ID
     * @param {Array<Object>} permissions - Array of permission objects with access/edit/delete flags
     * @returns {Promise<Object>} Success response
     */
    async assignPermissionsToRole(roleId, permissions) {
        try {
            // Ensure backend schema `{ permissions: [ {permission_id, granted} ] }`
            const normalized = permissions
                .map((p) => {
                    if (typeof p === 'string') return { permission_id: p, granted: true };
                    return { permission_id: p.permission_id || p.id, granted: p.granted ?? true };
                })
                .filter((p) => p.permission_id != null && p.permission_id !== ''); // Filter out invalid permission IDs

            console.log('Normalized permissions for role', roleId, ':', normalized);

            const response = await apiClient.put(`${this.rolesUrl}/${roleId}/permissions`, {
                permissions: normalized,
            });
            return unwrap(response);
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to assign permissions"
            };
        }
    }

    /**
     * Remove permissions from role
     * @param {string} roleId - Role ID
     * @param {Array<string>} permissionIds - Array of permission IDs
     * @returns {Promise<Object>} Success response
     */
    async removePermissionsFromRole(roleId, permissionIds) {
        try {
            const response = await apiClient.delete(`${this.rolesUrl}/${roleId}/permissions`, {
                data: { permission_ids: permissionIds }
            });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // PERMISSION MANAGEMENT
    // ============================================================================

    /**
     * Get all permissions
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Transformed permissions response
     */
    async getPermissions(params = {}) {
        try {
            // Remove null/undefined values
            const sanitizedParams = Object.fromEntries(
                Object.entries(params).filter(([_, value]) => value != null)
            );
            const response = await apiClient.get(this.permissionsUrl, { params: sanitizedParams });
            const payload = unwrap(response);
            
            if (payload.success && payload.data && Array.isArray(payload.data.items) && payload.data.meta) {
                 return {
                    success: true,
                    data: payload.data.items,
                    message: payload.message,
                    ...payload.data.meta
                };
            }
            
            // Legacy fallback
            const list = payload.data?.permissions || payload.data || [];
            const total = payload.data?.total_count || payload.data?.total || list.length;

            return { success: payload.success !== false, data: list, total, message: payload.message || "Permissions retrieved" };
        } catch (error) {
            return {
                success: false,
                data: [],
                message: error.response?.data?.detail || "Failed to fetch permissions"
            };
        }
    }

    /**
     * Get permissions by module
     * @param {string} module - Module name
     * @returns {Promise<Object>} Transformed permissions response
     */
    async getPermissionsByModule(module) {
        try {
            // Sanitize module parameter
            const sanitizedParams = module ? { module } : {};
            const response = await apiClient.get(this.permissionsUrl, { params: sanitizedParams });
            const payload = unwrap(response);

            if (payload.success && payload.data && Array.isArray(payload.data.items)) {
                 return {
                    success: true,
                    data: payload.data.items,
                    message: payload.message,
                    ...(payload.data.meta || {})
                };
            }
            
            // Legacy fallback
            const list = payload.data?.permissions || payload.data || [];
            return { success: payload.success !== false, data: list, message: payload.message || "Permissions retrieved" };
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Create new permission
     * @param {Object} permissionData - Permission data
     * @returns {Promise<Object>} Transformed permission response
     */
    async createPermission(permissionData) {
        try {
            const modernData = {
                name: permissionData.name,
                description: permissionData.description,
                module: permissionData.module,
                action: permissionData.action,
                resource: permissionData.resource
            };
            const response = await apiClient.post(this.permissionsUrl, modernData);
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Update existing permission
     * @param {string} permissionId - Permission ID
     * @param {Object} permissionData - Updated permission data
     * @returns {Promise<Object>} Transformed permission response
     */
    async updatePermission(permissionId, permissionData) {
        try {
            const modernData = {
                name: permissionData.name,
                description: permissionData.description,
                module: permissionData.module,
                action: permissionData.action,
                resource: permissionData.resource
            };
            const response = await apiClient.put(`${this.permissionsUrl}/${permissionId}`, modernData);
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Delete permission
     * @param {string} permissionId - Permission ID
     * @returns {Promise<Object>} Success response
     */
    async deletePermission(permissionId) {
        try {
            await apiClient.delete(`${this.permissionsUrl}/${permissionId}`);
            return { success: true, message: "Permission deleted successfully" };
        } catch (error) {
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // USER ROLE ASSIGNMENT
    // ============================================================================

    /**
     * Assign role to user
     * @param {string} userId - User ID
     * @param {string} roleId - Role ID
     * @returns {Promise<Object>} Success response
     */
    async assignRoleToUser(userId, roleId) {
        try {
            const response = await apiClient.post(`/users/${userId}/roles`, {
                role_id: roleId
            });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Remove role from user
     * @param {string} userId - User ID
     * @param {string} roleId - Role ID
     * @returns {Promise<Object>} Success response
     */
    async removeRoleFromUser(userId, roleId) {
        try {
            const response = await apiClient.delete(`/rbac/users/${userId}/roles/${roleId}`);
            return {
                success: true,
                data: response.data,
                message: "Role removed from user successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to remove role from user"
            };
        }
    }

    /**
     * Update user roles (replace all existing roles with new ones)
     * @param {string} userId - User ID
     * @param {Array<string>} roleIds - Array of role IDs
     * @returns {Promise<Object>} Success response
     */
    async updateUserRoles(userId, roleIds) {
        try {
            // First get existing roles
            const existingRoles = await this.getUserRoles(userId);

            // Remove existing roles
            if (existingRoles.success && existingRoles.data.length > 0) {
                for (const role of existingRoles.data) {
                    await this.removeRoleFromUser(userId, role.role_id || role.id);
                }
            }

            // Assign new roles
            const results = [];
            for (const roleId of roleIds) {
                const result = await this.assignRoleToUser(userId, roleId);
                results.push(result);
            }

            return {
                success: true,
                data: results,
                message: "User roles updated successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update user roles"
            };
        }
    }

    /**
     * Get user roles
     * @param {string} userId - User ID
     * @param {Object} options - Optional parameters (bustCache: boolean)
     * @returns {Promise<Object>} Transformed roles response
     */
    async getUserRoles(userId, options = {}) {
        try {
            // Validate userId
            if (!userId) {
                throw new Error('User ID is required');
            }
            const key = `user_roles_${userId}`;

            // Skip cache if bustCache option is true
            if (!options.bustCache) {
                const cached = this.getCached(key);
                if (cached) return cached;
            }

            const response = await this.dedupe(key, () => apiClient.get(`/users/${userId}/roles`));
            
            // Handle the wrapped response format
            if (response.data && response.data.success) {
                const result = {
                    success: true,
                    data: response.data.data || {},
                    message: response.data.message || "User roles fetched successfully"
                };
                this.setCached(key, result);
                return result;
            }
            
            const result = {
                success: true,
                data: response.data || [],
                message: "User roles fetched successfully"
            };
            this.setCached(key, result);
            return result;
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Assign role to user
     * @param {string} userId - User ID
     * @param {string} roleId - Role ID to assign
     * @param {Object} options - Additional options
     * @returns {Promise<Object>} Assignment result
     */
    async assignRoleToUser(userId, roleId, options = {}) {
        try {
            if (!userId || !roleId) {
                throw new Error('User ID and Role ID are required');
            }

            const requestData = {
                role_id: roleId,
                conditions: options.conditions || {},
                valid_from: options.valid_from,
                valid_until: options.valid_until
            };

            const response = await apiClient.post(`/users/${userId}/roles`, requestData);

            // Clear the user roles cache to force refresh on next fetch
            const cacheKey = `user_roles_${userId}`;
            this.cache.delete(cacheKey);

            return {
                success: true,
                data: response.data?.data || response.data,
                message: response.data?.message || "Role assigned successfully"
            };
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Remove role from user
     * @param {string} userId - User ID
     * @param {string} roleId - Role ID to remove
     * @returns {Promise<Object>} Removal result
     */
    async removeRoleFromUser(userId, roleId) {
        try {
            if (!userId || !roleId) {
                throw new Error('User ID and Role ID are required');
            }

            const response = await apiClient.delete(`/users/${userId}/roles/${roleId}`);

            // Clear the user roles cache to force refresh on next fetch
            const cacheKey = `user_roles_${userId}`;
            this.cache.delete(cacheKey);

            return {
                success: true,
                data: response.data?.data || response.data,
                message: response.data?.message || "Role removed successfully"
            };
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Update user roles (bulk assignment)
     * @param {string} userId - User ID
     * @param {Array<string>} roleIds - Array of role IDs to assign
     * @returns {Promise<Object>} Update result
     */
    async updateUserRoles(userId, roleIds) {
        try {
            if (!userId || !Array.isArray(roleIds)) {
                throw new Error('User ID and role IDs array are required');
            }

            // Get current user roles
            const currentRolesResponse = await this.getUserRoles(userId);
            const currentRoles = currentRolesResponse.data?.roles || [];
            const currentRoleIds = currentRoles.map(role => role.role_id);

            // Determine roles to add and remove
            const rolesToAdd = roleIds.filter(roleId => !currentRoleIds.includes(roleId));
            const rolesToRemove = currentRoleIds.filter(roleId => !roleIds.includes(roleId));

            // Process removals
            for (const roleId of rolesToRemove) {
                await this.removeRoleFromUser(userId, roleId);
            }

            // Process additions
            for (const roleId of rolesToAdd) {
                await this.assignRoleToUser(userId, roleId);
            }

            return {
                success: true,
                message: `Successfully updated roles for user`,
                data: {
                    added: rolesToAdd,
                    removed: rolesToRemove,
                    final_roles: roleIds
                }
            };
        } catch (error) {
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // PERMISSION CHECKING
    // ============================================================================

    /**
     * Check if user has permission
     * @param {string} userId - User ID
     * @param {string} permission - Permission name
     * @returns {Promise<Object>} Permission check response
     */
    async checkUserPermission(userId, permission) {
        try {
            // Validate parameters
            if (!userId) {
                throw new Error('User ID is required');
            }
            if (!permission) {
                throw new Error('Permission is required');
            }
            const response = await apiClient.get(`/rbac/users/${userId}/permissions/check`, {
                params: { permission_id: permission }
            });
            return unwrap(response);
        } catch (error) {
            throw handleApiError(error);
        }
    }

    /**
     * Get user permissions
     * @param {string} userId - User ID
     * @returns {Promise<Object>} User permissions response
     */
    async getUserPermissions(userId) {
        try {
            // Validate userId
            if (!userId) {
                throw new Error('User ID is required');
            }
            const key = `user_perms_${userId}`;
            const cached = this.getCached(key);
            if (cached) return cached;
            const response = await this.dedupe(key, () => apiClient.get(`/rbac/users/${userId}/permissions`));
            const result = unwrap(response);
            this.setCached(key, result);
            return result;
        } catch (error) {
            return handleApiError(error);
        }
    }

    /**
     * Get comprehensive user permissions including roles, system permissions, and form permissions
     * @param {string} userId - User ID
     * @returns {Promise<Object>} Comprehensive permissions response
     */
    async getUserComprehensivePermissions(userId) {
        try {
            if (!userId) {
                throw new Error('User ID is required');
            }
            const key = `user_comprehensive_${userId}`;
            const cached = this.getCached(key);
            if (cached) return cached;

            // SIMPLIFIED: Just use the permissions endpoint which returns everything we need
            const userPermissionsResponse = await this.dedupe(`perms_${userId}`, () =>
                apiClient.get(`/rbac/users/${userId}/permissions`)
            );

            // Handle nested data structure - API returns { data: { data: { ... } } }
            // After axios unwrapping, we get { data: { effective_permissions: ... } }
            const apiData = userPermissionsResponse.data?.data || userPermissionsResponse.data || {};

            console.log('🔍 Extracting permissions data:', {
                hasNestedData: !!userPermissionsResponse.data?.data,
                effectivePermissionsCount: Object.keys(apiData.effective_permissions || {}).length,
                rolesCount: (apiData.roles || []).length
            });

            // Return in the expected format
            const result = {
                success: true,
                data: {
                    user_id: userId,
                    effective_permissions: apiData.effective_permissions || {},
                    permissions: apiData.permissions || [],
                    permissions_by_category: apiData.permissions_by_category || {},
                    roles: apiData.roles || [],
                    form_permissions: [] // Will be computed from effective_permissions if needed
                }
            };

            this.setCached(key, result);
            return result;

        } catch (error) {
            console.error('❌ getUserComprehensivePermissions error:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            return {
                success: false,
                data: {},
                message: error.response?.data?.detail || error.message || "Failed to fetch user permissions"
            };
        }
    }

    /**
     * Check if user has permission for a specific form action
     * @param {Object} userPermissions - User's permissions
     * @param {string} action - Action to check (view_forms, edit_forms, etc.)
     * @param {Object} form - Form object
     * @returns {boolean} Whether user has permission
     */
    checkFormPermission(userPermissions, action, form) {
        // Check form-specific permissions using the actual permission format
        const effectivePermissions = userPermissions.effective_permissions || {};
        
        // Map action names to permission codes
        const actionMapping = {
            'view_forms': 'view',
            'edit_forms': 'edit',
            'delete_forms': 'delete',
            'submit_forms': 'submit'
        };
        
        const permissionAction = actionMapping[action] || action;
        
        // Get form ID from multiple possible fields
        const formId = form?.form_id || form?.id;
        
        console.debug('🔍 Checking form permission:', {
            action,
            permissionAction,
            formId,
            effectivePermissions: Object.keys(effectivePermissions)
        });

        // Check 1: Wildcard permission (e.g., 'forms.*.edit')
        const wildcardPerm = `forms.*.${permissionAction}`;
        if (effectivePermissions[wildcardPerm]) {
            console.debug(`✅ Found wildcard permission: ${wildcardPerm}`);
            return true;
        }

        // Check 2: Form-specific permission (e.g., 'forms.{formId}.edit')
        if (formId) {
            const specificPerm = `forms.${formId}.${permissionAction}`;
            if (effectivePermissions[specificPerm]) {
                console.debug(`✅ Found specific permission: ${specificPerm}`);
                return true;
            }
        }

        // Check 3: Legacy general permission (e.g., 'edit_forms')
        if (effectivePermissions[action]) {
            console.debug(`✅ Found legacy permission: ${action}`);
            return true;
        }

        // Check 4: Department-specific permissions (legacy)
        const departmentPermission = `${action}_${form?.department_name?.toLowerCase()}`;
        if (departmentPermission && effectivePermissions[departmentPermission]) {
            console.debug(`✅ Found department permission: ${departmentPermission}`);
            return true;
        }

        // Check 5: General form submission/view permissions
        if (permissionAction === 'edit' && effectivePermissions['form.submit']) {
            console.debug('✅ Found general form.submit permission');
            return true;
        }

        console.debug(`❌ No permission found for action: ${action} (${permissionAction})`);
        return false;
    }

    /**
     * Update user's form permissions
     * @param {string} userId - User ID
     * @param {Array} formPermissions - Array of form permission objects
     * @returns {Promise<Object>} Update response
     */
    async updateUserFormPermissions(userId, formPermissions) {
        try {
            if (!userId) {
                throw new Error('User ID is required');
            }

            // Transform form permissions to API format
            const permissionUpdates = formPermissions.map(fp => ({
                resource_type: 'form',
                resource_id: fp.form_id,
                permissions: {
                    view: fp.can_view,
                    edit: fp.can_edit,
                    delete: fp.can_delete,
                    submit: fp.can_submit
                }
            }));

            const response = await apiClient.put(`/rbac/users/${userId}/form-permissions`, {
                form_permissions: permissionUpdates
            });

            return {
                success: true,
                data: response.data,
                message: "Form permissions updated successfully"
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update form permissions"
            };
        }
    }

    // ============================================================================
    // LEGACY COMPATIBILITY METHODS
    // ============================================================================

    /**
     * Legacy method: Get role list
     * @param {Object} params - Legacy parameters
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getRoleListAction(params = {}) {
        return this.getRoles(params);
    }

    /**
     * Legacy method: Save role (create or update)
     * @param {Object} roleData - Role data
     * @returns {Promise<Object>} Legacy formatted response
     */
    async saveRoleAction(roleData) {
        if (roleData.id) {
            return this.updateRole(roleData.id, roleData);
        } else {
            return this.createRole(roleData);
        }
    }

    /**
     * Legacy method: Delete role
     * @param {string} roleId - Role ID
     * @returns {Promise<Object>} Legacy formatted response
     */
    async deleteRoleAction(roleId) {
        return this.deleteRole(roleId);
    }

    /**
     * Legacy method: Get permissions list
     * @param {Object} params - Legacy parameters
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getPermissionsListAction(params = {}) {
        return this.getPermissions(params);
    }

    /**
     * Legacy method: Get role permissions
     * @param {string} roleId - Role ID
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getRolePermissionsAction(roleId) {
        return this.getRoleById(roleId);
    }

    /**
     * Legacy method: Assign permissions to role
     * @param {string} roleId - Role ID
     * @param {Array<string>} permissions - Permission IDs
     * @returns {Promise<Object>} Legacy formatted response
     */
    async assignPermissionsAction(roleId, permissions) {
        return this.assignPermissionsToRole(roleId, permissions);
    }

    /**
     * Legacy method: Get user group (role) list
     * @param {Object} params - Legacy parameters
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getUserGroupListAction(params = {}) {
        return this.getRoles(params);
    }

    /**
     * Legacy method: Save user group (role)
     * @param {Object} groupData - Group data
     * @returns {Promise<Object>} Legacy formatted response
     */
    async saveUserGroupAction(groupData) {
        // Transform user group data to role data
        const roleData = {
            name: groupData.name || groupData.groupName,
            description: groupData.description,
            permissions: groupData.permissions || [],
            status: groupData.status
        };

        if (groupData.id) {
            return this.updateRole(groupData.id, roleData);
        } else {
            return this.createRole(roleData);
        }
    }

    // ============================================================================
    // FORM PERMISSIONS (LEGACY COMPATIBILITY)
    // ============================================================================

    /**
     * Get form list with permissions for a user/role
     * @param {Object} params - Parameters including userId or roleId
     * @returns {Promise<Object>} Form permissions response
     */
    async getFormListWithPermissions(params) {
        try {
            const { userId, _cacheBuster } = params;
            if (!userId) {
                throw new Error('User ID is required');
            }

            // Prepare query parameters for cache busting
            const queryParams = {};
            if (_cacheBuster) {
                queryParams._t = _cacheBuster; // Add timestamp for cache busting
            }

            // Call the correct endpoint for user-specific permissions
            const response = await apiClient.get(`/users/${userId}/permissions`, { 
                params: queryParams 
            });
            
            // Handle the wrapped response format (backend returns success envelope)
            let permissionCodes = [];
            let responseData = response.data;
            
            // Extract permission codes from the nested structure
            if (responseData && responseData.success && responseData.data) {
                // The API returns: { data: { permissions: { permissions: [...] } } }
                if (responseData.data.permissions && Array.isArray(responseData.data.permissions.permissions)) {
                    permissionCodes = responseData.data.permissions.permissions;
                } else if (responseData.data.permissions && Array.isArray(responseData.data.permissions)) {
                    permissionCodes = responseData.data.permissions;
                } else if (Array.isArray(responseData.data)) {
                    permissionCodes = responseData.data;
                }
            } else if (responseData && Array.isArray(responseData.permissions)) {
                permissionCodes = responseData.permissions;
            } else if (responseData && Array.isArray(responseData)) {
                permissionCodes = responseData;
            }

            console.log('Raw permission codes from backend:', permissionCodes);

            // Transform permission codes to rich permission objects using our definitions
            const formPermissions = permissionCodes.map(permissionCode => {
                return this.transformPermissionCode(permissionCode);
            });

            console.log('Transformed form permissions:', formPermissions);

            return {
                success: true,
                data: formPermissions,
                message: "User permissions retrieved successfully"
            };
        } catch (error) {
            console.error('Error in getFormListWithPermissions:', error);
            return {
                success: false,
                data: [],
                message: error.response?.data?.detail || "Failed to fetch form permissions"
            };
        }
    }

    /**
     * Permission definitions map based on backend seed data
     * This maps permission codes to their detailed information including actions
     */
    getPermissionDefinitions() {
        return {
            // ============================================================================
            // USER MANAGEMENT PERMISSIONS
            // ============================================================================
            "manage_users": {
                name: "Manage Users",
                description: "Create, update, and delete user accounts",
                category: "user_management",
                resource_type: "user",
                actions: ["create", "read", "update", "delete"],
                is_system: true
            },
            "view_users": {
                name: "View Users", 
                description: "View user information and profiles",
                category: "user_management",
                resource_type: "user",
                actions: ["read"],
                is_system: true
            },
            "assign_roles": {
                name: "Assign Roles",
                description: "Assign roles to users and manage role assignments",
                category: "user_management", 
                resource_type: "role_assignment",
                actions: ["create", "read", "update", "delete"],
                is_system: true
            },
            "manage_roles": {
                name: "Manage Roles",
                description: "Create, update, and delete roles",
                category: "user_management",
                resource_type: "role", 
                actions: ["create", "read", "update", "delete"],
                is_system: true
            },
            "view_master_data": {
                name: "View Master Data",
                description: "View system master data and reference information",
                category: "user_management",
                resource_type: "master_data",
                actions: ["read"],
                is_system: true
            },
            "manage_permissions": {
                name: "Manage Permissions",
                description: "Create, update, and delete system permissions",
                category: "user_management",
                resource_type: "permission",
                actions: ["create", "read", "update", "delete"],
                is_system: true
            },

            // ============================================================================
            // PATIENT MANAGEMENT PERMISSIONS
            // ============================================================================
            "view_patient_info": {
                name: "View Patient Information",
                description: "View patient demographic and medical information",
                category: "patient_management",
                resource_type: "patient",
                actions: ["read"],
                is_system: true
            },
            "update_patient_info": {
                name: "Update Patient Information", 
                description: "Update patient demographic and medical information",
                category: "patient_management",
                resource_type: "patient",
                actions: ["read", "update"],
                is_system: true
            },
            "create_patient": {
                name: "Create Patient",
                description: "Create new patient records",
                category: "patient_management",
                resource_type: "patient", 
                actions: ["create"],
                is_system: true
            },
            "delete_patient": {
                name: "Delete Patient",
                description: "Delete patient records (soft delete)",
                category: "patient_management",
                resource_type: "patient",
                actions: ["delete"],
                is_system: true
            },
            "view_patient_search": {
                name: "Search Patients",
                description: "Search and filter patient records",
                category: "patient_management",
                resource_type: "patient",
                actions: ["read", "search"],
                is_system: true
            },

            // ============================================================================
            // CLINICAL OPERATIONS PERMISSIONS  
            // ============================================================================
            "medpass:create": {
                name: "Create MedPass Workflow",
                description: "Create new medication administration workflows",
                category: "clinical_operations",
                resource_type: "medpass",
                actions: ["create"],
                is_system: true
            },
            "medpass:read": {
                name: "Read MedPass Workflow",
                description: "View medication administration workflows",
                category: "clinical_operations", 
                resource_type: "medpass",
                actions: ["read"],
                is_system: true
            },
            "medpass:update": {
                name: "Update MedPass Workflow",
                description: "Update medication administration workflows",
                category: "clinical_operations",
                resource_type: "medpass",
                actions: ["read", "update"],
                is_system: true
            },
            "medpass:delete": {
                name: "Delete MedPass Workflow", 
                description: "Delete medication administration workflows",
                category: "clinical_operations",
                resource_type: "medpass",
                actions: ["delete"],
                is_system: true
            },
            "medpass:approve": {
                name: "Approve MedPass Workflow",
                description: "Approve medication administration workflows",
                category: "clinical_operations",
                resource_type: "medpass", 
                actions: ["approve"],
                is_system: true
            },
            "medpass:administer": {
                name: "Administer Medication",
                description: "Administer medications to patients",
                category: "clinical_operations",
                resource_type: "medpass",
                actions: ["administer"],
                is_system: true
            },

            // ============================================================================
            // FORM MANAGEMENT PERMISSIONS
            // ============================================================================
            "forms.*.edit": {
                name: "Edit All Forms",
                description: "Edit any form in the system",
                category: "clinical_operations",
                resource_type: "form",
                actions: ["read", "update"],
                is_system: true
            },
            "forms.*.view": {
                name: "View All Forms",
                description: "View any form in the system",
                category: "clinical_operations",
                resource_type: "form",
                actions: ["read"],
                is_system: true
            },
            "forms.*.create": {
                name: "Create Forms",
                description: "Create new forms",
                category: "clinical_operations",
                resource_type: "form",
                actions: ["create"],
                is_system: true
            },
            "forms.*.delete": {
                name: "Delete Forms",
                description: "Delete forms",
                category: "clinical_operations",
                resource_type: "form",
                actions: ["delete"],
                is_system: true
            },
            "form.view": {
                name: "View Forms",
                description: "View and read forms",
                category: "clinical_operations",
                resource_type: "form",
                actions: ["read"],
                is_system: true
            },
            "form.create": {
                name: "Create Forms",
                description: "Create new forms",
                category: "clinical_operations", 
                resource_type: "form",
                actions: ["create"],
                is_system: true
            },
            "form.edit": {
                name: "Edit Forms",
                description: "Edit existing forms",
                category: "clinical_operations",
                resource_type: "form", 
                actions: ["update"],
                is_system: true
            },
            "form.submit": {
                name: "Submit Forms",
                description: "Submit form responses",
                category: "clinical_operations",
                resource_type: "form",
                actions: ["submit"],
                is_system: true
            },

            // ============================================================================
            // DYNAMIC FORMS PERMISSIONS
            // ============================================================================
            "dynamic_forms:view_submissions": {
                name: "View Form Submissions",
                description: "View submitted dynamic form responses",
                category: "clinical_operations",
                resource_type: "dynamic_form",
                actions: ["read"],
                is_system: true
            },
            "dynamic_forms:create": {
                name: "Create Dynamic Forms",
                description: "Create new dynamic forms",
                category: "clinical_operations",
                resource_type: "dynamic_form",
                actions: ["create"],
                is_system: true
            },
            "dynamic_forms:submit": {
                name: "Submit Dynamic Forms",
                description: "Submit dynamic form responses",
                category: "clinical_operations",
                resource_type: "dynamic_form",
                actions: ["create", "submit"],
                is_system: true
            },

            // ============================================================================
            // DOCUMENT MANAGEMENT PERMISSIONS
            // ============================================================================
            "document_verify": {
                name: "Verify Documents",
                description: "Verify uploaded documents",
                category: "document_management",
                resource_type: "document",
                actions: ["verify"],
                is_system: true
            },
            "document_upload": {
                name: "Upload Documents",
                description: "Upload patient documents",
                category: "document_management",
                resource_type: "document",
                actions: ["create"],
                is_system: true
            },
            "document_view": {
                name: "View Documents",
                description: "View patient documents",
                category: "document_management",
                resource_type: "document",
                actions: ["read"],
                is_system: true
            },
            "document_manage": {
                name: "Manage Documents",
                description: "Full document management capabilities",
                category: "document_management",
                resource_type: "document",
                actions: ["create", "read", "update", "delete"],
                is_system: true
            },

            // ============================================================================
            // NOTIFICATION PERMISSIONS
            // ============================================================================
            "notifications:read_all": {
                name: "Read All Notifications",
                description: "Read notifications for all users",
                category: "administrative",
                resource_type: "notification",
                actions: ["read"],
                is_system: true
            },
            "notifications:manage_templates": {
                name: "Manage Notification Templates",
                description: "Create and manage notification templates",
                category: "administrative",
                resource_type: "notification_template",
                actions: ["create", "read", "update", "delete"],
                is_system: true
            },
            "notifications:send": {
                name: "Send Notifications",
                description: "Send notifications to users",
                category: "administrative",
                resource_type: "notification",
                actions: ["create", "submit"],
                is_system: true
            },

            // ============================================================================
            // MASTER DATA PERMISSIONS
            // ============================================================================
            "manage_master_data": {
                name: "Manage Master Data",
                description: "Manage system master data and reference information",
                category: "system_configuration",
                resource_type: "master_data",
                actions: ["create", "read", "update", "delete"],
                is_system: true
            },
            "view_departments": {
                name: "View Departments",
                description: "View department information",
                category: "administrative",
                resource_type: "department",
                actions: ["read"],
                is_system: true
            },
            "manage_departments": {
                name: "Manage Departments",
                description: "Create, update, and delete departments",
                category: "administrative",
                resource_type: "department",
                actions: ["create", "read", "update", "delete"],
                is_system: true
            },

            // ============================================================================
            // AUDIT PERMISSIONS
            // ============================================================================
            "audit_view": {
                name: "View Audit Logs",
                description: "View system audit logs",
                category: "audit",
                resource_type: "audit",
                actions: ["read"],
                is_system: true
            },
            "audit_export": {
                name: "Export Audit Logs",
                description: "Export audit logs for compliance",
                category: "audit",
                resource_type: "audit",
                actions: ["read", "export"],
                is_system: true
            },
            "audit_manage": {
                name: "Manage Audit Settings",
                description: "Manage audit configuration and settings",
                category: "audit",
                resource_type: "audit",
                actions: ["read", "update"],
                is_system: true
            },
            "patient_audit_view": {
                name: "View Patient Audit Logs",
                description: "View patient-specific audit logs",
                category: "audit",
                resource_type: "audit",
                actions: ["read"],
                is_system: true
            },

            // ============================================================================
            // SYSTEM CONFIGURATION PERMISSIONS
            // ============================================================================
            "system_configuration": {
                name: "System Configuration",
                description: "Configure system settings and parameters",
                category: "system_configuration",
                resource_type: "system",
                actions: ["read", "update"],
                is_system: true
            },
            "view_access_logs": {
                name: "View Access Logs",
                description: "View system access logs and analytics",
                category: "system_configuration",
                resource_type: "access_log",
                actions: ["read"],
                is_system: true
            },
            "manage_system_settings": {
                name: "Manage System Settings",
                description: "Configure and manage system settings",
                category: "system_configuration",
                resource_type: "system_settings",
                actions: ["read", "update"],
                is_system: true
            },

            // ============================================================================
            // DASHBOARD PERMISSIONS
            // ============================================================================
            "dashboard:admin": {
                name: "View Admin Dashboard",
                description: "Access administrative dashboard and analytics",
                category: "reporting",
                resource_type: "dashboard",
                actions: ["read", "analytics"],
                is_system: true
            },
            "dashboard:user": {
                name: "View User Dashboard",
                description: "Access user dashboard",
                category: "reporting",
                resource_type: "dashboard",
                actions: ["read"],
                is_system: true
            },

            // ============================================================================
            // REPORTING PERMISSIONS
            // ============================================================================
            "reports:view": {
                name: "View Reports",
                description: "View system reports",
                category: "reporting",
                resource_type: "report",
                actions: ["read"],
                is_system: true
            },
            "reports:generate": {
                name: "Generate Reports",
                description: "Generate and create new reports",
                category: "reporting",
                resource_type: "report",
                actions: ["create", "read"],
                is_system: true
            },
            "reports:export": {
                name: "Export Reports",
                description: "Export reports to various formats",
                category: "reporting",
                resource_type: "report",
                actions: ["read", "export"],
                is_system: true
            }
        };
    }

    /**
     * Transform permission code to rich permission object
     * @param {string} permissionCode - Permission code from API
     * @returns {Object} Rich permission object with actions, description, etc.
     */
    transformPermissionCode(permissionCode) {
        const definitions = this.getPermissionDefinitions();
        const definition = definitions[permissionCode];
        
        if (definition) {
            return {
                id: permissionCode,
                name: definition.name,
                description: definition.description,
                category: definition.category,
                resource_type: definition.resource_type,
                actions: definition.actions,
                is_active: true,
                is_system: definition.is_system,
                // Add formatted category name
                departmentName: this.formatCategoryName(definition.category),
                departmentId: definition.category,
                // Legacy compatibility
                isAccess: 1,
                isEdit: definition.actions.includes('update') || definition.actions.includes('edit') ? 1 : 0,
                isDelete: definition.actions.includes('delete') ? 1 : 0,
                canAccess: true,
                canEdit: definition.actions.includes('update') || definition.actions.includes('edit'),
                canDelete: definition.actions.includes('delete')
            };
        }
        
        // Fallback for unknown permission codes
        return {
            id: permissionCode,
            name: this.formatPermissionName(permissionCode),
            description: `Permission: ${permissionCode}`,
            category: this.inferCategoryFromCode(permissionCode),
            resource_type: 'unknown',
            actions: ['read'], // Default action
            is_active: true,
            is_system: false,
            departmentName: this.formatCategoryName(this.inferCategoryFromCode(permissionCode)),
            departmentId: this.inferCategoryFromCode(permissionCode),
            isAccess: 1,
            isEdit: 0,
            isDelete: 0,
            canAccess: true,
            canEdit: false,
            canDelete: false
        };
    }

    /**
     * Format permission code to readable name
     * @param {string} code - Permission code
     * @returns {string} Formatted name
     */
    formatPermissionName(code) {
        return code
            .split(/[._:]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    /**
     * Infer category from permission code
     * @param {string} code - Permission code
     * @returns {string} Inferred category
     */
    inferCategoryFromCode(code) {
        const lowerCode = code.toLowerCase();
        
        if (lowerCode.includes('user') || lowerCode.includes('role') || lowerCode.includes('permission')) {
            return 'user_management';
        }
        if (lowerCode.includes('patient')) {
            return 'patient_management';
        }
        if (lowerCode.includes('form') || lowerCode.includes('medpass')) {
            return 'clinical_operations';
        }
        if (lowerCode.includes('document')) {
            return 'document_management';
        }
        if (lowerCode.includes('audit')) {
            return 'audit';
        }
        if (lowerCode.includes('report') || lowerCode.includes('dashboard')) {
            return 'reporting';
        }
        if (lowerCode.includes('notification')) {
            return 'administrative';
        }
        if (lowerCode.includes('system') || lowerCode.includes('config')) {
            return 'system_configuration';
        }
        
        return 'administrative';
    }

    /**
     * Format category names for better UI display
     * @param {string} categoryName - Raw category name 
     * @returns {string} Formatted category name
     */
    formatCategoryName(categoryName) {
        if (!categoryName || categoryName === 'General') return 'General';
        
        // Handle specific mappings first
        const categoryMappings = {
            'clinical_operations': 'Clinical Operations',
            'patient_management': 'Patient Management', 
            'medication_management': 'Medication Management',
            'staff_management': 'Staff Management',
            'system_administration': 'System Administration',
            'reporting_analytics': 'Reporting & Analytics',
            'billing_finance': 'Billing & Finance',
            'quality_assurance': 'Quality Assurance',
            'compliance_audit': 'Compliance & Audit',
            'emergency_management': 'Emergency Management',
            'inventory_supplies': 'Inventory & Supplies',
            'appointment_scheduling': 'Appointment & Scheduling',
            'user_management': 'User Management',
            'administrative': 'Administrative',
            'system_configuration': 'System Configuration',
            'reporting': 'Reporting',
            'audit': 'Audit',
            'document_management': 'Document Management',
            'scheduling': 'Scheduling'
        };

        // Check for exact mapping first
        const lowerCaseName = categoryName.toLowerCase();
        if (categoryMappings[lowerCaseName]) {
            return categoryMappings[lowerCaseName];
        }

        // Generic transformation: snake_case or kebab-case to Title Case
        return categoryName
            .split(/[_-]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    /**
     * Update user permissions (for staff management)
     * @param {Object} permissionData - Permission update data
     * @returns {Promise<Object>} Success response
     */
    async updateUserPermissions(permissionData) {
        try {
            const { userId, formsPermission } = permissionData;

            // For now, we'll assign permissions directly to user roles
            // This is a simplified approach - in a full implementation,
            // we'd need to handle user-specific permissions

            const response = await apiClient.post(`/rbac/users/${userId}/permissions`, {
                permissions: formsPermission
            });

            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.detail || "Failed to update user permissions"
            };
        }
    }

    /**
     * Get user role assignments
     * @param {string} userId - User ID
     * @returns {Promise<Object>} User roles response
     */
    async getUserRoleAssignments(userId) {
        try {
            // Validate userId
            if (!userId) {
                throw new Error('User ID is required');
            }
            const response = await apiClient.get(`/rbac/users/${userId}/roles`);
            return {
                success: true,
                data: response.data,
                message: "User roles retrieved successfully"
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                message: error.response?.data?.detail || "Failed to fetch user roles"
            };
        }
    }
}

// Export singleton instance
export default new RBACService(); 